'use client';

import { useMemo, useState } from 'react';
import CalculatorInputs from './CalculatorInputs';
import ResultsDisplay from './ResultsDisplay';
import { calculateDividendPerShare } from './calculations';
import { DividendPerShareValues } from './types';
import CurrencySelector from '@/components/utils/CurrencySelector';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  translations: ToolsTranslations['dividendPerShare'];
}

export default function DividendPerShareCalculator({ translations }: Props) {
  const [values, setValues] = useState<DividendPerShareValues>({
    totalDividend: 4000000,
    sharesOutstanding: 2000000,
    currency: 'kr',
  });

  const onValuesChange = (field: keyof DividendPerShareValues, value: number | string) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const result = useMemo(() => calculateDividendPerShare(values), [values]);

  return (
    <section id="dividend-per-share-calculator" className="w-full my-12 tools">
      <div className="rounded-xl border border-gray-200 shadow-sm bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-blue-900">{translations.title}</h2>
              <p className="text-sm text-gray-600 mt-1">{translations.subtitle}</p>
            </div>
            <div className="w-56">
              <CurrencySelector
                value={values.currency}
                onChange={(currency) => onValuesChange('currency', currency)}
                showArrow={false}
                localStorageKey="currencyPreference"
              />
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <CalculatorInputs values={values} onValuesChange={onValuesChange} translations={translations} />
            <ResultsDisplay result={result} currency={values.currency} translations={translations} />
          </div>
        </div>
      </div>
    </section>
  );
}


